import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import PlayerProfile from './PlayerProfile';

function Players({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const usersData = snapshot.docs
        .map(doc => ({
          uid: doc.id,
          ...doc.data()
        }))
        .filter(user => user.status === 'approved')
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setUsers(usersData);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (selectedPlayer) {
    return (
      <PlayerProfile 
        userId={selectedPlayer} 
        onClose={() => setSelectedPlayer(null)} 
      />
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>👥 Players</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingState}>Loading players...</div>
          ) : (
            <div style={styles.playersGrid}>
              {filteredUsers.map(user => (
                <div 
                  key={user.uid} 
                  style={styles.playerCard}
                  onClick={() => setSelectedPlayer(user.uid)}
                >
                  <div style={styles.photoContainer}>
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.name} style={styles.photo} />
                    ) : (
                      <div style={styles.photoPlaceholder}>
                        {user.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div style={styles.playerInfo}>
                    <div style={styles.playerName}>{user.name}</div>
                    {user.role === 'admin' && <div style={styles.roleBadge}>Admin</div>}
                    {user.role === 'officer' && user.officerTitle && <div style={styles.roleBadge}>{user.officerTitle}</div>}
                    {user.role === 'moderator' && user.officerTitle && <div style={styles.modBadge}>{user.officerTitle}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'slideUp 0.25s ease'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  searchContainer: {
    padding: '20px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '30px'
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  playersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '20px'
  },
  playerCard: {
    backgroundColor: '#0f0f0f',
    border: '2px solid #d4af37',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  photoContainer: {
    marginBottom: '15px',
    display: 'flex',
    justifyContent: 'center'
  },
  photo: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #d4af37',
    boxShadow: '0 4px 8px rgba(0,0,0,0.4)'
  },
  photoPlaceholder: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#0f0f0f',
    border: '2px solid #d4af37',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: '700',
    color: '#d4af37',
    boxShadow: '0 4px 8px rgba(0,0,0,0.4)'
  },
  playerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  playerName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  playerHandicap: {
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '600'
  },
  roleBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    marginTop: '5px'
  },
  modBadge: {
    backgroundColor: '#6366f1',
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '700',
    marginTop: '5px'
  },
};

export default Players;